import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ChevronDown, Loader2, Megaphone, X, Home, Building2, MapPin, Facebook, Instagram,
} from "lucide-react";

const API = getApiUrl();

const PLATFORMS = [
  { key: "rightmove", label: "Rightmove", icon: Home },
  { key: "zoopla", label: "Zoopla", icon: Building2 },
  { key: "prime_location", label: "Prime Location", icon: MapPin },
  { key: "facebook", label: "Facebook", icon: Facebook },
  { key: "instagram", label: "Instagram", icon: Instagram },
];

// Mirrors AdminDashboard.jsx's buildErrorDialog so agents get the same
// rich missing-fields / raw-response error dialog as Master Admin.
const buildErrorDialog = (provider, environment, resp) => {
  const missing = Array.isArray(resp?.missing_fields) ? resp.missing_fields : [];
  let summary = resp?.message || resp?.detail || "";
  const responseText = resp?.response_text || "";
  if (!summary && responseText) {
    try {
      const parsed = JSON.parse(responseText);
      if (parsed.error_advice) summary = parsed.error_advice;
      else if (Array.isArray(parsed.errors) && parsed.errors[0]?.message) {
        summary = parsed.errors[0].message.slice(0, 300);
      }
    } catch (_) { /* not JSON */ }
  }
  return {
    provider,
    environment,
    httpStatus: resp?.http_status || resp?.status_code || null,
    title: missing.length ? `${provider} push blocked — fix required field${missing.length > 1 ? "s" : ""}` : `${provider} push failed`,
    summary: summary || "Push failed — see response details below.",
    missing,
    url: resp?.url || "",
    request_body: resp?.request_body || null,
    response_text: responseText,
  };
};

export function AdvertisingSection({ propertyId }) {
  const [open, setOpen] = useState(false);
  const [statusMap, setStatusMap] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const [pushError, setPushError] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await axios.get(
        `${API}/property-admin/property/${propertyId}/advertising`,
        { withCredentials: true }
      );
      const map = {};
      (data.platforms || []).forEach((p) => {
        map[`${p.platform}:${p.environment}`] = p;
      });
      setStatusMap(map);
    } catch (e) {
      console.error("Failed to load advertising status:", e);
    }
  }, [propertyId]);

  useEffect(() => { if (open) fetchStatus(); }, [open, fetchStatus]);

  const platformLabel = (key) => PLATFORMS.find((p) => p.key === key)?.label || key;

  const handlePush = async (platform, environment) => {
    const key = `${platform}:${environment}`;
    setBusyKey(key);
    try {
      const { data } = await axios.post(
        `${API}/property-admin/property/${propertyId}/advertising/push`,
        { platform, environment },
        { withCredentials: true, timeout: 90000 }
      );
      if (data.status === "success") {
        toast.success(`${platformLabel(platform)} (${environment.toUpperCase()}): ${data.message || "pushed"}`);
      } else {
        toast.error(`${platformLabel(platform)} (${environment.toUpperCase()}) push failed — see details`);
        setPushError(buildErrorDialog(platformLabel(platform), environment, data));
      }
      await fetchStatus();
    } catch (e) {
      const errData = e.response?.data || {};
      toast.error(`${platformLabel(platform)} (${environment.toUpperCase()}) push failed — see details`);
      setPushError(buildErrorDialog(platformLabel(platform), environment, {
        message: errData.detail || errData.message || e.message,
        http_status: e.response?.status || null,
      }));
    } finally {
      setBusyKey(null);
    }
  };

  const handleRemove = async (platform, environment) => {
    if (!window.confirm(`Remove this property from ${platformLabel(platform)} (${environment.toUpperCase()})?`)) return;
    const key = `${platform}:${environment}:remove`;
    setBusyKey(key);
    try {
      const { data } = await axios.post(
        `${API}/property-admin/property/${propertyId}/advertising/remove`,
        { platform, environment },
        { withCredentials: true, timeout: 90000 }
      );
      if (data.status === "success") {
        toast.success(`Removed from ${platformLabel(platform)} (${environment.toUpperCase()})`);
      } else {
        toast.error(`Failed to remove from ${platformLabel(platform)} — see details`);
        setPushError(buildErrorDialog(platformLabel(platform), environment, data));
      }
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    } finally {
      setBusyKey(null);
    }
  };

  if (!propertyId) return null;

  return (
    <div className="space-y-4 pt-4 border-t border-border" data-testid="advertising-section">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full text-left"
        data-testid="advertising-section-toggle"
      >
        <div className="flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-primary" />
          <Label className="text-base font-medium cursor-pointer">Advertising</Label>
        </div>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <p className="text-sm text-muted-foreground -mt-2">
        Push this property to Rightmove, Zoopla, Prime Location, Facebook &amp; Instagram
      </p>

      {open && (
        <div className="space-y-3">
          {PLATFORMS.map(({ key: platform, label, icon: Icon }) => (
            <div
              key={platform}
              className="p-4 bg-muted/30 border border-border rounded-sm space-y-3"
              data-testid={`advertising-platform-${platform}`}
            >
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-sm">{label}</span>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {["live", "test"].map((env) => {
                  const rec = statusMap[`${platform}:${env}`];
                  const busy = busyKey === `${platform}:${env}`;
                  const removing = busyKey === `${platform}:${env}:remove`;
                  const pushed = rec?.status === "success";
                  return (
                    <div key={env} className="flex items-center gap-2" data-testid={`advertising-${platform}-${env}`}>
                      <Button
                        type="button"
                        variant={env === "live" ? "default" : "outline"}
                        size="sm"
                        className="rounded-none text-xs"
                        disabled={busy}
                        onClick={() => handlePush(platform, env)}
                        data-testid={`advertising-push-${platform}-${env}`}
                      >
                        {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                        {pushed ? `Re-push ${env === "live" ? "Live" : "Test"}` : `Push ${env === "live" ? "Live" : "Test"}`}
                      </Button>
                      {rec && (
                        <>
                          <span
                            className={`text-[11px] px-2 py-1 rounded-sm ${pushed ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                            data-testid={`advertising-status-${platform}-${env}`}
                          >
                            {pushed ? "Pushed" : "Failed"}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="rounded-none text-xs text-destructive hover:text-destructive"
                            disabled={removing}
                            onClick={() => handleRemove(platform, env)}
                            data-testid={`advertising-remove-${platform}-${env}`}
                          >
                            {removing ? <Loader2 className="h-3 w-3 animate-spin" /> : "Remove"}
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Push error dialog — mirrors Master Admin's rich error view */}
      <Dialog open={!!pushError} onOpenChange={(o) => { if (!o) setPushError(null); }}>
        <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="advertising-push-error-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <X size={18} className="text-red-600" />
              {pushError?.title || "Push failed"}
            </DialogTitle>
            <DialogDescription>
              {pushError?.provider} · {pushError?.environment?.toUpperCase()}
              {pushError?.httpStatus ? ` · HTTP ${pushError.httpStatus}` : ""}
            </DialogDescription>
          </DialogHeader>
          {pushError && (
            <div className="space-y-3 text-sm">
              {pushError.missing?.length > 0 && (
                <div className="border-l-4 border-amber-500 bg-amber-50 p-3 rounded space-y-2">
                  <div className="font-semibold text-amber-900">
                    Missing required field{pushError.missing.length > 1 ? "s" : ""}
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-amber-900 text-sm">
                    {pushError.missing.map((f, i) => (
                      <li key={i} data-testid={`advertising-push-error-missing-${i}`}>{f}</li>
                    ))}
                  </ul>
                  <div className="text-xs text-amber-800 pt-1">
                    Fill these in and try again, or ask your Admin to update them.
                  </div>
                </div>
              )}
              <div className="border-l-4 border-red-400 bg-red-50 p-3 rounded">
                <div className="text-xs font-semibold text-red-900 uppercase tracking-wide mb-1">
                  Error details
                </div>
                <div className="text-sm text-red-900 whitespace-pre-wrap break-words">
                  {pushError.summary}
                </div>
              </div>
              {pushError.url && (
                <div className="text-[11px] text-muted-foreground break-all">
                  <span className="uppercase tracking-wide font-medium mr-1">Endpoint:</span>
                  <code>{pushError.url}</code>
                </div>
              )}
              {pushError.request_body && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Request body sent
                  </summary>
                  <pre className="text-[10px] mt-2 max-h-64 overflow-auto bg-muted p-2 rounded">
                    {JSON.stringify(pushError.request_body, null, 2)}
                  </pre>
                </details>
              )}
              {pushError.response_text && (
                <details className="text-xs" open={!pushError.missing?.length}>
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Raw response
                  </summary>
                  <pre className="text-[10px] mt-2 max-h-64 overflow-auto bg-muted p-2 rounded whitespace-pre-wrap">
                    {pushError.response_text}
                  </pre>
                </details>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPushError(null)} data-testid="advertising-push-error-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdvertisingSection;
